import { useState, useCallback } from "react";
import { CardOfTheDayStatus } from "../types/CardOfTheDayStatus";
import { AstroPrognosisStatus } from "../types/AstroPrognosisStatus";
import { ZodiacalPrognosisStatus } from "../types/ZodiacalPrognosisStatus";
import { LuckyCardStatus } from "../types/LuckyCardStatus";
import { HomeInfoDomain } from "@/types/HomeInfo";
import { formatTimeToNext } from "../utils/timeUtils";
import { contentUpdatingManager } from "@/api/contentUpdatingManager";
import { getMillisecondsUntilReset } from "@/utils/timerUtils";

export const useStatusManagement = () => {
  const [cardOfTheDayStatus, setCardOfTheDayStatus] = useState<CardOfTheDayStatus | null>(null);
  const [astroPrognosisStatus, setAstroPrognosisStatus] = useState<AstroPrognosisStatus | null>(null);
  const [zodiacalPrognosisStatus, setZodiacalPrognosisStatus] = useState<ZodiacalPrognosisStatus | null>(null);
  const [luckyCardStatus, setLuckyCardStatus] = useState<LuckyCardStatus | null>(null);
  const [aboutText, setAboutText] = useState<string>("");

  const updateCardOfTheDayStatus = useCallback((homeInfo: HomeInfoDomain) => {
    const resetDateRaw = homeInfo?.cardOfTheDay?.resetDate;
    const card = homeInfo.cardOfTheDay;

    if (card === null) {
      setCardOfTheDayStatus({ isAvailable: true, timeToNextCard: "Доступно", card: null });
      return;
    }

    if (!resetDateRaw || isNaN(resetDateRaw.getTime())) {
      setCardOfTheDayStatus({ isAvailable: false, timeToNextCard: "Нет данных", card });
      return;
    }

    const diff = getMillisecondsUntilReset(resetDateRaw);
    const timeToNextCard = formatTimeToNext(diff, "Открытие через");
    
    setCardOfTheDayStatus({
      isAvailable: diff <= 0,
      timeToNextCard,
      card: card,
    });
  }, []);

  const updateAboutText = useCallback((aboutText: string) => {
    setAboutText(aboutText);
  }, []);

  const updateAstroPrognosisStatus = useCallback((homeInfo?: HomeInfoDomain) => {
    const resetDate =
      homeInfo?.generalForecastResetDate ??
      contentUpdatingManager.getAstroPrognosisDataLastUpdating();

    if (resetDate === null) {
      setAstroPrognosisStatus({ isAvailable: true, timeToNextCard: "Доступно" });
      return;
    }

    const diff = getMillisecondsUntilReset(resetDate);
    const timeToNextCard = formatTimeToNext(diff, "До следующего прогноза");
    
    setAstroPrognosisStatus({
      isAvailable: diff <= 0,
      timeToNextCard,
    });
  }, []);

  const updateZodiacalPrognosisStatus = useCallback((isSubscribed: boolean, homeInfo?: HomeInfoDomain) => {
    if (!isSubscribed) {
      setZodiacalPrognosisStatus({ isAvailable: false, timeToNextCard: "", isSubscribed: false });
      return;
    }

    const resetDate =
      homeInfo?.personalForecastResetDate ??
      contentUpdatingManager.getZodiacPrognosisDataLastUpdating();

    if (resetDate === null) {
      setZodiacalPrognosisStatus({ isAvailable: true, timeToNextCard: "Доступно", isSubscribed: true });
      return;
    }

    const diff = getMillisecondsUntilReset(resetDate);
    const timeToNextCard = formatTimeToNext(diff, "До следующего прогноза");

    setZodiacalPrognosisStatus({
      isAvailable: diff <= 0,
      timeToNextCard,
      isSubscribed: true,
    });
  }, []);

  const updateLuckyCardStatus = useCallback((homeInfo: HomeInfoDomain, isSubscribed: boolean) => {
    if (!isSubscribed) {
      setLuckyCardStatus({
        isAvailable: false,
        timeToNextCard: "",
        isSubscribed: false,
        selectedCard: null,
        luckyCard: null,
      });
      return;
    }

    const resetDateRaw = homeInfo?.luckyCardResetDate;
    const selectedCard = homeInfo.selectedLuckyCard;
    const luckyCard = homeInfo.luckyCard;

    if (resetDateRaw === null) {
      setLuckyCardStatus({
        isAvailable: true,
        timeToNextCard: "Доступно",
        selectedCard,
        luckyCard,
        isSubscribed: true,
      });
      return;
    }

    const diff = getMillisecondsUntilReset(resetDateRaw);
    const timeToNextCard = formatTimeToNext(diff, "До следующей карты");
    
    setLuckyCardStatus({
      isAvailable: diff <= 0,
      timeToNextCard,
      selectedCard,
      luckyCard,
      isSubscribed: true,
    });
  }, []);

  return {
    cardOfTheDayStatus,
    astroPrognosisStatus,
    zodiacalPrognosisStatus,
    luckyCardStatus,
    aboutText,
    updateCardOfTheDayStatus,
    updateAstroPrognosisStatus,
    updateZodiacalPrognosisStatus,
    updateLuckyCardStatus,
    updateAboutText,
  };
};
